import { useQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/lib/auth"
import { formatAmount, formatPercent } from "@/lib/format"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"

import { fetchBaseDeclarada, fetchGastosPorProveedor, fetchPnlByCountry } from "@/routes/finance/api"
import { useFinanceFilters } from "@/routes/finance/context"
import { DesglosePage } from "@/routes/finance/DesglosePage"
import { nombrePais } from "@/routes/finance/mapping"
import { fetchPeople } from "@/routes/masters/api"

/**
 * UNA PESTAÑA PARA LAS CUATRO CUENTAS, no cuatro pestañas.
 *
 * Revenue Build Up, Costo de ventas, Gastos y Payroll eran cuatro entradas del
 * menú de Finanzas y las cuatro eran `DesglosePage` con otra cuenta: la misma
 * tabla, las mismas tres cifras, el mismo lápiz. Doce pestañas en una fila no
 * son un índice, son una línea de ruido —y tres de ellas no añadían una sola
 * pregunta nueva.
 *
 * Ahora la cuenta se elige AQUÍ, con un control segmentado, y la pestaña es
 * una. El recorrido natural —ventas, qué me cuesta venderlas, qué me cuesta la
 * estructura, qué me cuesta el equipo— queda en una sola pantalla y en el
 * orden del P&L.
 *
 * La cuenta viaja en la URL (`?cuenta=`), así que un enlace a «Gastos» sigue
 * abriendo Gastos, y las rutas antiguas (`/revenue`, `/costo-ventas`,
 * `/gastos`, `/payroll`) redirigen aquí con su cuenta puesta.
 *
 * «COSTO DE EQUIPO» Y NO «PAYROLL». Había dos «Payroll» en el producto: éste,
 * que son las cifras AGREGADAS del modelo, y Contabilidad › Planilla, que es la
 * liquidación persona por persona. Se llamaban igual y no eran lo mismo. La
 * tarjeta del P&L ya decía «Costo de equipo»: ahora esto se llama igual.
 */

export type CuentaDesglosada = "ventas" | "costo-ventas" | "gastos" | "equipo"

interface Vista {
  id: CuentaDesglosada
  /** Cuenta del modelo, tal como la nombra la hoja. */
  account: string
  label: string
  descripcion: string
  footnote: string
}

const VISTAS: Vista[] = [
  {
    id: "ventas",
    account: "Revenue",
    label: "Ventas",
    descripcion: "Por línea de negocio",
    footnote: "Aquí las ventas van por LÍNEA DE NEGOCIO. El corte por CLIENTE, desde los contratos, está en «Cómo se arma el forecast».",
  },
  {
    id: "costo-ventas",
    account: "Cost of Goods Sold (COGS)",
    label: "Costo de ventas",
    descripcion: "Lo que cuesta entregar el servicio, por línea de negocio",
    footnote: "El costo de ventas está partido por línea (B2B Lead Gen / Staffing) en casi todos sus conceptos: SDRs, comisiones, software, hosting, processing fee y team leads. Esa separación es lo que permite ver el margen de cada línea por separado, y por eso conviene no consolidar los conceptos aunque se llamen igual.",
  },
  {
    id: "gastos",
    account: "G&A Expenses",
    label: "Gastos",
    descripcion: "Estructura: contabilidad, oficina, software, legal, banco y viajes",
    footnote: "Los gastos de contabilidad están separados por país (Accounting Pe / Mx / Br / Pa) porque cada sociedad paga su propio estudio. Con todas las sociedades se ven los cuatro; con una elegida, solo el suyo.",
  },
  {
    id: "equipo",
    account: "Labor Costs",
    label: "Costo de equipo",
    descripcion: "Costo total del equipo: sueldos, comisiones y cargas sociales",
    footnote: "Estas cifras vienen AGREGADAS del modelo financiero: son el costo mensual del equipo, no una liquidación por persona. Renta 5, AFP y EsSalud aparecen como líneas propias porque así los lleva el modelo. La planilla persona por persona está en Contabilidad › Planilla.",
  },
]

const POR_DEFECTO: CuentaDesglosada = "ventas"

function esCuenta(v: string | null): v is CuentaDesglosada {
  return VISTAS.some((x) => x.id === v)
}

export function DesgloseDeCuentasPage() {
  const [params, setParams] = useSearchParams()
  const elegida = params.get("cuenta")
  const cuenta: CuentaDesglosada = esCuenta(elegida) ? elegida : POR_DEFECTO
  const vista = VISTAS.find((v) => v.id === cuenta) ?? VISTAS[0]

  const elegir = (id: CuentaDesglosada) => {
    const siguientes = new URLSearchParams(params)
    if (id === POR_DEFECTO) siguientes.delete("cuenta")
    else siguientes.set("cuenta", id)
    setParams(siguientes, { replace: true })
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label id="cuenta-desglosada-label">{t("Cuenta")}</Label>
        <div
          role="radiogroup"
          aria-labelledby="cuenta-desglosada-label"
          data-guia="cuenta-desglosada"
          className="inline-flex items-center gap-0.5 rounded-md border bg-surface p-0.5"
        >
          {VISTAS.map((v) => {
            const activo = v.id === cuenta
            return (
              <button
                key={v.id}
                type="button"
                role="radio"
                aria-checked={activo}
                onClick={() => elegir(v.id)}
                className={cn(
                  "h-6 rounded-[3px] px-2 text-xs font-medium transition-colors duration-100",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  activo
                    ? "bg-surface-sunken text-foreground shadow-xs"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t(v.label)}
              </button>
            )
          })}
        </div>
      </div>

      {/* `key`: cambiar de cuenta reinicia las líneas abiertas y el aviso de edición. */}
      <DesglosePage
        key={vista.id}
        account={vista.account}
        titulo={t(vista.label)}
        descripcion={t(vista.descripcion)}
        footnote={t(vista.footnote)}
      />

      {cuenta === "ventas" ? <VentasPorPais /> : null}
      <SegunTusMaestros cuenta={cuenta} />
    </div>
  )
}

const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"]

/**
 * SEGÚN TUS MAESTROS: el mismo concepto, por quién.
 *
 * Cesar, 2026-10-04: «en ventas todos los nombres de cliente con monto, en
 * costo de ventas cada línea debería de abrir para ver el gasto de cada persona
 * asociada, luego en softwares igual». La tabla de arriba es la HOJA y va por
 * línea de negocio: no sabe de clientes ni de personas ni de proveedores. Lo
 * que sí lo sabe es el maestro y el libro, y eso es lo que va aquí, debajo y
 * con su propia fuente dicha:
 *
 *   ventas          → por CLIENTE, desde los contratos (lo que se firmó)
 *   costo de ventas → por PROVEEDOR, desde los comprobantes de compra con
 *                     cuenta de costo; y las PERSONAS de costo de ventas
 *   gastos          → por PROVEEDOR, desde los comprobantes con cuenta de gasto
 *   costo de equipo → por PERSONA, desde el maestro (bruto mensual)
 *
 * No tiene por qué cuadrar con la hoja, y se dice: son dos fuentes. Cuando se
 * declare desde qué mes manda el maestro (Qué va a Finanzas), ésta pasa a ser
 * la cifra y la hoja la referencia.
 */
function SegunTusMaestros({ cuenta }: { cuenta: CuentaDesglosada }) {
  if (cuenta === "ventas") return <VentasPorCliente />
  if (cuenta === "equipo") return <EquipoPorPersona naturaleza={null} />
  return (
    <>
      <PorProveedor tipo={cuenta === "costo-ventas" ? "costo" : "gasto"} />
      {cuenta === "costo-ventas" ? <EquipoPorPersona naturaleza="costo_venta" /> : null}
    </>
  )
}

function FilaDeMeses({ etiqueta, sub, meses, total }: {
  etiqueta: string; sub?: string; meses: Record<string, number>; total: number
}) {
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap">
        {etiqueta}{sub ? <span className="ml-1.5 text-2xs text-muted-foreground">{sub}</span> : null}
      </TableCell>
      {MESES_CORTOS.map((_, i) => {
        const v = meses[String(i + 1)] ?? 0
        return <TableCell key={i} className={cn("num", v === 0 && "num-zero")}>{v ? formatAmount(v) : "—"}</TableCell>
      })}
      <TableCell className="num font-medium">{formatAmount(total)}</TableCell>
    </TableRow>
  )
}

function CabeceraDeMeses({ primera }: { primera: string }) {
  return (
    <TableHeader>
      <TableRow>
        <TableHead className="min-w-[14rem]">{primera}</TableHead>
        {MESES_CORTOS.map((m) => <TableHead key={m} className="w-20 text-right">{m}</TableHead>)}
        <TableHead className="w-28 text-right">{t("Total")}</TableHead>
      </TableRow>
    </TableHeader>
  )
}

function VentasPorCliente() {
  const { activeTenantId } = useAuth()
  const { anio, pais, paisQuery } = useFinanceFilters()
  const q = useQuery({
    queryKey: ["finance-declarado", activeTenantId, anio, pais],
    queryFn: () => fetchBaseDeclarada(anio, paisQuery),
    enabled: Boolean(activeTenantId),
  })
  const clientes = [...(q.data?.fuentes.contratos.clientes ?? [])]
    .map((c) => {
      const meses: Record<string, number> = {}
      for (let m = 1; m <= 12; m++) meses[String(m)] = (c.meses[String(m)] ?? 0) + (c.meses_renovacion[String(m)] ?? 0)
      return { ...c, mesesTotales: meses, total: Object.values(meses).reduce((s, v) => s + v, 0) }
    })
    .sort((a, b) => b.total - a.total)
  return (
    <WorkPanel
      title={t("Según tus maestros: ventas por cliente")}
      meta={<span className="text-2xs text-muted-foreground">{t("Desde los contratos firmados y sus renovaciones · USD")}</span>}
      footnote={t("Es lo que se firmó, cliente por cliente. La tabla de arriba es tu hoja por línea de negocio: no tienen por qué dar lo mismo. Para dar de alta un cliente o un contrato nuevo, Maestros › Contratos o «Cómo se arma el forecast».")}
    >
      <DataState loading={q.isLoading} error={q.error} empty={clientes.length === 0}
                 emptyMessage={t("No hay contratos activos: las ventas por cliente salen de ahí.")}
                 onRetry={() => void q.refetch()} rows={4}>
        <div className="overflow-x-auto">
          <Table>
            <CabeceraDeMeses primera={t("Cliente")} />
            <TableBody>
              {clientes.map((c) => (
                <FilaDeMeses key={`${c.cliente}-${c.linea ?? ""}`} etiqueta={c.cliente}
                             sub={`${nombrePais(c.pais)}${c.linea ? ` · ${c.linea}` : ""}`}
                             meses={c.mesesTotales} total={c.total} />
              ))}
            </TableBody>
          </Table>
        </div>
      </DataState>
    </WorkPanel>
  )
}

function PorProveedor({ tipo }: { tipo: "gasto" | "costo" }) {
  const { activeTenantId } = useAuth()
  const { anio, pais, paisQuery } = useFinanceFilters()
  const q = useQuery({
    queryKey: ["finance-por-proveedor", activeTenantId, anio, pais, tipo],
    queryFn: () => fetchGastosPorProveedor(anio, tipo, paisQuery),
    enabled: Boolean(activeTenantId),
  })
  const d = q.data
  return (
    <WorkPanel
      title={tipo === "costo" ? t("Según tu libro: costo de ventas por proveedor") : t("Según tu libro: gastos por proveedor")}
      meta={<span className="text-2xs text-muted-foreground">{t("Desde los comprobantes de compra, por el mapeo de su cuenta · USD")}{d?.sin_cotizacion ? ` · ${d.sin_cotizacion} ${t("sin cotización")}` : ""}</span>}
      footnote={d?.nota}
    >
      <DataState loading={q.isLoading} error={q.error}
                 empty={Boolean(d) && (d?.proveedores.length ?? 0) === 0 && !(d?.sin_clasificar.documentos)}
                 emptyMessage={t("Todavía no hay comprobantes de compra con esta naturaleza este año.")}
                 emptyHint={t("Llegan por correo, por Drive o subidos a mano, y toman su naturaleza al aprobarse en Revisión.")}
                 onRetry={() => void q.refetch()} rows={4}>
        <div className="overflow-x-auto">
          <Table>
            <CabeceraDeMeses primera={t("Proveedor")} />
            <TableBody>
              {(d?.proveedores ?? []).map((p) => (
                <FilaDeMeses key={p.tax_id} etiqueta={p.proveedor}
                             sub={`${p.documentos} ${t("comprobante(s)")}${p.grupos.length ? ` · ${p.grupos.join(", ")}` : ""}`}
                             meses={p.meses} total={p.total} />
              ))}
              {d?.sin_clasificar.documentos ? (
                <FilaDeMeses etiqueta={t("Sin cuenta todavía")}
                             sub={`${d.sin_clasificar.documentos} ${t("comprobante(s) en Revisión")}`}
                             meses={d.sin_clasificar.meses} total={d.sin_clasificar.total} />
              ) : null}
            </TableBody>
          </Table>
        </div>
      </DataState>
    </WorkPanel>
  )
}

function EquipoPorPersona({ naturaleza }: { naturaleza: "costo_venta" | null }) {
  const { activeTenantId } = useAuth()
  const { pais } = useFinanceFilters()
  const q = useQuery({ queryKey: ["people"], queryFn: fetchPeople, enabled: Boolean(activeTenantId) })
  const personas = (q.data ?? [])
    .filter((p) => p.status === "activo")
    .filter((p) => !pais || p.entityCountry === pais)
    .filter((p) => !naturaleza || p.nature === naturaleza)
    .sort((a, b) => b.monthlyGross - a.monthlyGross)
  const porMoneda = personas.reduce<Record<string, number>>((acc, p) => {
    acc[p.currency] = (acc[p.currency] ?? 0) + p.monthlyGross
    return acc
  }, {})
  return (
    <WorkPanel
      title={naturaleza ? t("Según tu maestro: las personas de costo de ventas") : t("Según tu maestro: el equipo, persona por persona")}
      meta={<span className="text-2xs text-muted-foreground">{t("Bruto mensual, en la moneda de cada una · sin cargas")}</span>}
      footnote={t("Lo que cuesta cada persona al mes según el maestro. Las cargas (EsSalud, CTS, aportes) las calcula la planilla: Contabilidad › Planilla. Para dar de alta, de baja o cambiar un sueldo, Maestros › Personas.")}
    >
      <DataState loading={q.isLoading} error={q.error} empty={personas.length === 0}
                 emptyMessage={t("No hay personas activas con este corte.")} onRetry={() => void q.refetch()} rows={4}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Persona")}</TableHead>
              <TableHead className="w-44">{t("Sociedad")}</TableHead>
              <TableHead className="w-36">{t("Area")}</TableHead>
              <TableHead className="w-28">{t("Naturaleza")}</TableHead>
              <TableHead className="w-32 text-right">{t("Bruto mensual")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {personas.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">{p.fullName}</TableCell>
                <TableCell className="truncate text-2xs text-muted-foreground">{p.entityName}</TableCell>
                <TableCell className="truncate text-2xs text-muted-foreground">{p.costCenterName || "—"}</TableCell>
                <TableCell className="text-2xs">{p.nature === "costo_venta" ? t("Costo de ventas") : p.nature === "capex" ? "Capex" : t("Gasto")}</TableCell>
                <TableCell className="num">{formatAmount(p.monthlyGross)} <span className="text-2xs text-muted-foreground">{p.currency}</span></TableCell>
              </TableRow>
            ))}
            {Object.entries(porMoneda).map(([mon, total]) => (
              <TableRow key={mon} className="bg-surface-sunken font-medium">
                <TableCell colSpan={4}>{t("Total mensual")} · {mon}</TableCell>
                <TableCell className="num">{formatAmount(total)} <span className="text-2xs font-normal text-muted-foreground">{mon}</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DataState>
    </WorkPanel>
  )
}

/**
 * Dónde se generan las ventas. Sólo con todas las sociedades: con una elegida
 * sería una sola fila.
 */
function VentasPorPais() {
  const { activeTenantId } = useAuth()
  const { anio, pais } = useFinanceFilters()

  const porPais = useQuery({
    queryKey: ["finance-por-pais", activeTenantId, anio],
    queryFn: () => fetchPnlByCountry(anio),
    enabled: Boolean(activeTenantId) && !pais,
  })

  if (pais) return null

  return (
    <WorkPanel
      title={t("Por país")}
      meta={<span className="text-2xs text-muted-foreground">{t("Dónde se generan las ventas")}</span>}
    >
      <DataState
        loading={porPais.isLoading}
        error={porPais.error}
        empty={(porPais.data ?? []).length === 0}
        emptyMessage={`${t("No hay ventas por país para")} ${anio}.`}
        onRetry={() => void porPais.refetch()}
        rows={4}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("País")}</TableHead>
              <TableHead className="w-32 text-right">{t("Ventas")}</TableHead>
              <TableHead className="w-28 text-right">{t("% del total")}</TableHead>
              <TableHead className="w-32 text-right">{t("Margen bruto")}</TableHead>
              <TableHead className="w-32 text-right">{t("EBITDA")}</TableHead>
              <TableHead className="w-24 text-right">{t("Margen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(porPais.data ?? []).map((row) => (
              <TableRow key={row.pais}>
                <TableCell className="font-medium">{nombrePais(row.pais)}</TableCell>
                <TableCell className="num">{formatAmount(row.ingresos)}</TableCell>
                <TableCell className="num text-muted-foreground">
                  {row.participacion_ingresos === null
                    ? "" : formatPercent(row.participacion_ingresos, 1)}
                </TableCell>
                <TableCell className="num">{formatAmount(row.margen_bruto)}</TableCell>
                <TableCell className={cn("num font-medium", row.ebitda < 0 && "text-destructive")}>
                  {formatAmount(row.ebitda)}
                </TableCell>
                <TableCell className="num">
                  {row.ebitda_pct === null ? "" : (
                    <Badge variant={row.ebitda < 0 ? "destructive-soft" : "success-soft"}>
                      {formatPercent(row.ebitda_pct, 1)}
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DataState>
    </WorkPanel>
  )
}
